import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { PrimaryButton } from '../ui/PrimaryButton';
import { GhostButton } from '../ui/GhostButton';

export const ToDoAddModal = ({ isOpen, onClose, onAdd, onEdit, taskToEdit, isReadOnly, groups, charges, statuses, products, onProductSelect }) => {
    const [task, setTask] = useState('');
    const [groupId, setGroupId] = useState('');
    const [productId, setProductId] = useState('');
    const [chargeId, setChargeId] = useState('');
    const [status, setStatus] = useState('Pending');
    const [dueDate, setDueDate] = useState('');
    const [notes, setNotes] = useState('');

    React.useEffect(() => {
        if (taskToEdit) {
            setTask(taskToEdit.task || '');
            setGroupId(taskToEdit.group_id || '');
            setProductId(taskToEdit.product_id || '');
            setChargeId(taskToEdit.charge_id || '');
            setStatus(taskToEdit.status || 'Pending');
            setDueDate(taskToEdit.due_date ? taskToEdit.due_date.split('T')[0] : '');
            setNotes(taskToEdit.notes || '');
        } else {
            // Reset if no task to edit (Add mode)
            setTask('');
            setGroupId('');
            setProductId('');
            setChargeId('');
            setStatus('Pending');
            setDueDate('');
            setNotes('');
        }
    }, [taskToEdit, isOpen]);

    const handleSubmit = async () => {
        if (!task) return;

        const taskData = {
            task,
            group_id: groupId || null,
            product_id: productId || null,
            charge_id: chargeId || null,
            status,
            due_date: dueDate || null,
            notes
        };

        if (taskToEdit) {
            await onEdit(taskToEdit.id, taskData);
        } else {
            await onAdd(taskData);
        }

        onClose();
    };

    const handleProductChange = (e) => {
        const pid = e.target.value;
        setProductId(pid);
        setChargeId(''); // Reset charge when product changes
        if (onProductSelect) {
            onProductSelect(pid);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isReadOnly ? "View Task" : (taskToEdit ? "Edit Task" : "Add New Task")} maxWidth="max-w-2xl">
            <div className="space-y-4">
                {/* Task Name */}
                <div className="space-y-1">
                    <label className="text-xs font-bold text-stone-500 uppercase">Task Description</label>
                    <Input
                        value={task}
                        onChange={(e) => setTask(e.target.value)}
                        placeholder="What needs to be done?"
                        autoFocus={!isReadOnly}
                        disabled={isReadOnly}
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    {/* Group */}
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-stone-500 uppercase">Group</label>
                        <select
                            value={groupId}
                            onChange={(e) => setGroupId(e.target.value)}
                            className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-600 appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isReadOnly}
                        >
                            <option value="">No Group</option>
                            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                    </div>

                    {/* Status */}
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-stone-500 uppercase">Status</label>
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-600 appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isReadOnly}
                        >
                            {statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                    </div>

                    {/* Product (Optional) */}
                    {products && (
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500 uppercase">Product</label>
                            <select
                                value={productId}
                                onChange={handleProductChange}
                                className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-600 appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isReadOnly}
                            >
                                <option value="">Global Task (No Product)</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.model_name} ({p.sku})</option>)}
                            </select>
                        </div>
                    )}

                    {/* Charge */}
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-stone-500 uppercase">Charge</label>
                        <select
                            value={chargeId}
                            onChange={(e) => setChargeId(e.target.value)}
                            className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-600 appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isReadOnly || (products && !productId)}
                        >
                            <option value="">All Charges</option>
                            {charges.map(c => <option key={c.id} value={c.id}>{c.batch_number || c.charge_id}</option>)}
                        </select>
                    </div>

                    {/* Due Date */}
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-stone-500 uppercase">Due Date</label>
                        <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} disabled={isReadOnly} />
                    </div>
                </div>

                {/* Notes (Large) */}
                <div className="space-y-1">
                    <label className="text-xs font-bold text-stone-500 uppercase">Notes</label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add detailed notes here..."
                        className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-600 min-h-[150px] resize-y disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isReadOnly}
                    />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t border-stone-700">
                    <GhostButton onClick={onClose}>Close</GhostButton>
                    {!isReadOnly && (
                        <PrimaryButton onClick={handleSubmit}>{taskToEdit ? "Save Changes" : "Add Task"}</PrimaryButton>
                    )}
                </div>
            </div>
        </Modal>
    );
};
